import Link from 'next/link';
import { SubscribeButton } from '../islands/SubscribeButton';

/**
 * Channel publisher chip rendered on the watch page. Avatar + name +
 * subscriber count + {@link SubscribeButton} island.
 */
export interface ChannelChipProps {
  channelSlug: string;
  channelName: string;
  channelAvatar?: string;
  subscriberCount?: number;
  channelHrefTemplate?: string;
  /** Show the subscribe button. Default true. */
  showSubscribe?: boolean;
}

function compactCount(n: number | undefined): string {
  if (typeof n !== 'number') return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function avatarHue(slug: string): number {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return h % 360;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function ChannelChip({
  channelSlug,
  channelName,
  channelAvatar,
  subscriberCount,
  channelHrefTemplate = '/tube/channels/{slug}',
  showSubscribe = true,
}: ChannelChipProps) {
  const href = channelHrefTemplate.replace('{slug}', encodeURIComponent(channelSlug));
  const hue = avatarHue(channelSlug);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Link
        href={href}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textDecoration: 'none',
          color: 'inherit',
        }}
        aria-label={`${channelName} channel`}
      >
        <div
          aria-hidden
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: channelAvatar ? '#000' : `hsl(${hue}, 65%, 55%)`,
            backgroundImage: channelAvatar ? `url(${channelAvatar})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: channelAvatar ? undefined : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-on-brand, #fff)',
            fontSize: 'var(--font-size-base, 15px)',
            fontWeight: 600,
          }}
        >
          {channelAvatar ? '' : initials(channelName)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--font-size-base, 15px)',
              fontWeight: 600,
              color: 'var(--ink-900, inherit)',
            }}
          >
            {channelName}
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
              <circle cx="12" cy="12" r="10" fill="#1d9bf0" />
              <path
                d="M8 12.5l3 3 5-6"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {typeof subscriberCount === 'number' && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--ink-500, #6b7280)',
                marginTop: 1,
              }}
            >
              {compactCount(subscriberCount)} subscribers
            </div>
          )}
        </div>
      </Link>
      {showSubscribe && <SubscribeButton channelSlug={channelSlug} />}
    </div>
  );
}
